// A minimal PDF writer (decision 2026-09-29: invite cards can be saved as PDF without a library).
// Every page is A4 portrait with one JPEG image; the PDF viewer does the JPEG decoding (DCTDecode).

const MM_TO_PT = 72 / 25.4;
export const A4_MM = { width: 210, height: 297 };

export interface PdfImage {
  /** JPEG file contents. */
  jpeg: Uint8Array;
  /** Pixel size of the JPEG. */
  pixelWidth: number;
  pixelHeight: number;
  /** Position and size on the page in millimetres, measured from the top left corner. */
  x: number;
  y: number;
  width: number;
  height: number;
}

const round = (n: number) => Math.round(n * 1000) / 1000;

/** Builds a PDF with one page per image. */
export function buildPdf(pages: PdfImage[]): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const offsets: number[] = [];
  let length = 0;
  const push = (data: Uint8Array | string) => {
    const bytes = typeof data === 'string' ? encoder.encode(data) : data;
    chunks.push(bytes);
    length += bytes.length;
  };
  // Object numbers: 1 catalog, 2 page tree, then per page: page, contents, image.
  const object = (id: number, body: string, stream?: Uint8Array) => {
    offsets[id] = length;
    push(`${id} 0 obj\n${body}\n`);
    if (stream) {
      push('stream\n');
      push(stream);
      push('\nendstream\n');
    }
    push('endobj\n');
  };

  const pageW = round(A4_MM.width * MM_TO_PT);
  const pageH = round(A4_MM.height * MM_TO_PT);
  const pageIds = pages.map((_, i) => 3 + i * 3);

  // The binary comment marks the file as binary for transfer programs.
  push('%PDF-1.4\n%âãÏÓ\n');
  object(1, '<< /Type /Catalog /Pages 2 0 R >>');
  object(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`);
  pages.forEach((page, i) => {
    const pageId = pageIds[i]!;
    const contentsId = pageId + 1;
    const imageId = pageId + 2;
    object(
      pageId,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] ` +
        `/Resources << /XObject << /Im0 ${imageId} 0 R >> >> /Contents ${contentsId} 0 R >>`,
    );
    // PDF coordinates start from the bottom left corner.
    const w = round(page.width * MM_TO_PT);
    const h = round(page.height * MM_TO_PT);
    const x = round(page.x * MM_TO_PT);
    const y = round((A4_MM.height - page.y - page.height) * MM_TO_PT);
    const contents = encoder.encode(`q ${w} 0 0 ${h} ${x} ${y} cm /Im0 Do Q`);
    object(contentsId, `<< /Length ${contents.length} >>`, contents);
    object(
      imageId,
      `<< /Type /XObject /Subtype /Image /Width ${page.pixelWidth} /Height ${page.pixelHeight} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.jpeg.length} >>`,
      page.jpeg,
    );
  });

  const objectCount = 3 + pages.length * 3;
  const xref = length;
  let table = `xref\n0 ${objectCount}\n0000000000 65535 f \n`;
  for (let id = 1; id < objectCount; id++) table += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`;
  push(table);
  push(`trailer\n<< /Size ${objectCount} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  const out = new Uint8Array(length);
  let at = 0;
  for (const chunk of chunks) {
    out.set(chunk, at);
    at += chunk.length;
  }
  return out;
}
